import {
  type Chart,
  type ChartEvent,
  type ChartMeta,
  type PointElement,
  type Scale,
  type Tick,
} from "chart.js";
import { formatInTimeZone } from "date-fns-tz";
import { countBy, groupBy, intersection, set, sumBy } from "lodash";
import moment from "moment";

import { ANNOTATIONS_DATE_FORMAT } from "@/constants/date";

import { type AnnotationDataset } from "../hooks/use-build-annotation-dataset";

import { buildMessageSvg } from "./annotation-svgs";

export const CUSTOM_ANNOTATION_ID = "annotation-plugin";

const MAX_ALLOWED_DATE_RANGE_FOR_ADDING_ANNOTATIONS = 30;

export const getIsAtMaxDateRange = (annotationData: AnnotationData[]) => {
  if (!annotationData.length) {
    return false;
  }

  const date = annotationData[0].date;

  return moment(date).isBefore(
    moment().subtract(MAX_ALLOWED_DATE_RANGE_FOR_ADDING_ANNOTATIONS, "days"),
  );
};

export const getTimeSeries = (chart: Chart): Scale | undefined => {
  const timeSeries = (chart.boxes as Scale[]).find(({ type }) =>
    ["timeseries", "time"].includes(type),
  );

  return timeSeries;
};

export const getXPosition = (chart: Chart, tick: Tick): number | undefined => {
  const timeSeries = getTimeSeries(chart);
  const x = timeSeries?.getPixelForTick((tick as any).$context.index);
  return x;
};

export const getAnnotationPoints = (
  chart: Chart & { _metasets?: (ChartMeta & { data: PointElement[] })[] },
) => {
  const annotationMetaset = (
    chart._metasets?.find((metaset) => metaset?.label === CUSTOM_ANNOTATION_ID)
      ?.data ?? []
  ).filter(({ $context }: any) => $context);
  return annotationMetaset as PointElement<any, any>[];
};

export type AnnotationData = {
  date: string;
  xPosition: number | undefined;
  showAnnotations?: () => void;
  createAnnotation?: () => void;
  point: PointElement;
  clusteredDates?: string[];
  clusteredAnnotationCount?: number;
  clusteredTrainingEventsCount?: number;
};

export const buildClusteredDates = (
  timeSeries: Scale,
  annotatedDates: string[],
): string[][] => {
  const ticks = timeSeries.ticks;

  if (!ticks.length) {
    return [];
  }

  const minDate = moment(timeSeries.min);
  const maxDate = moment.max([
    moment(timeSeries.max),
    ...annotatedDates
      .map((date) => moment(date))
      .filter((date) => date.isValid()),
  ]);

  const dates = [];
  const current = moment(minDate);

  while (current.isSameOrBefore(maxDate)) {
    dates.push(moment(current).toISOString().split("T")[0]);
    current.add(1, "day");
  }

  const dateRanges = [];

  for (let i = 0; i < ticks.length - 1; i++) {
    dateRanges.push({
      start: moment(ticks[i].value),
      end: moment(ticks[i + 1].value),
    });
  }

  dateRanges.push({
    start: moment(ticks[ticks.length - 1].value),
    end: moment(maxDate).add(1, "day"),
  });

  const clusteredDates = [];

  for (const range of dateRanges) {
    const cluster = [];

    for (const dateString of dates) {
      const date = moment(dateString);

      if (date.isSameOrAfter(range.start) && date.isBefore(range.end)) {
        cluster.push(dateString);
      }
    }

    if (cluster.length) {
      clusteredDates.push(cluster);
    }
  }

  return clusteredDates;
};

const getDateTicks = (timeSeries: Scale, timezone: string) => {
  const ticksData = timeSeries.ticks.map((tick) => {
    return {
      tick,
      tickDate: formatInTimeZone(
        new Date(tick.value),
        timezone,
        ANNOTATIONS_DATE_FORMAT,
      ),
    };
  });

  return ticksData.some((e) => e.tick.major)
    ? ticksData.filter((e) => e.tick.major)
    : ticksData;
};

export const getHasDateInCluster = (
  clusteredDate: string[],
  annotatedDates: string[],
) => {
  const overlap = intersection(clusteredDate, annotatedDates);
  return overlap.length > 0;
};

export const getAnnotationData = (
  chart: Chart,
  options?: { force: boolean },
): AnnotationData[] => {
  const timeSeries = getTimeSeries(chart);
  const annotationDataset = chart.data.datasets?.find(
    (meta) => meta.label === CUSTOM_ANNOTATION_ID,
  ) as AnnotationDataset | undefined;

  // only show the cached version if we are not forcing it
  if (annotationDataset?.metadata.viewable && !options?.force) {
    return annotationDataset?.metadata.viewable;
  }

  if (annotationDataset?.metadata && timeSeries) {
    const chartData: AnnotationData[] = [];
    const {
      showAnnotations,
      createAnnotation,
      annotatedDates,
      datesUsedForTraining,
      timezone,
    } = annotationDataset.metadata;

    const annotatedDatesCount = countBy(annotatedDates);
    const trainingDatesCount = countBy(datesUsedForTraining);
    const annotationPoints = getAnnotationPoints(chart);
    const groupedAnnotationPoints = groupBy(
      annotationPoints,
      (point: any) => point.$context.raw.date,
    );
    const clusteredDates = buildClusteredDates(timeSeries, annotatedDates);
    const dateTicks = getDateTicks(timeSeries, timezone);

    dateTicks.forEach(({ tickDate }, index: number) => {
      const hasDate = getHasDateInCluster(
        clusteredDates[index],
        annotatedDates,
      );

      if (hasDate) {
        const clusteredAnnotationCount = sumBy(
          clusteredDates[index],
          (date) => annotatedDatesCount[date] ?? 0,
        );
        const clusteredTrainingEventsCount = sumBy(
          clusteredDates[index],
          (date) => trainingDatesCount[date] ?? 0,
        );

        chartData.push({
          date: tickDate,
          xPosition: undefined,
          showAnnotations: () =>
            showAnnotations({
              startDate: clusteredDates[index][0],
              endDate: clusteredDates[index][clusteredDates[index].length - 1],
            }),
          point: groupedAnnotationPoints[tickDate]?.[0] ?? ({} as PointElement),
          clusteredAnnotationCount,
          clusteredTrainingEventsCount,
          clusteredDates: intersection(annotatedDates, clusteredDates[index]),
        });
      } else {
        chartData.push({
          date: tickDate,
          xPosition: undefined,
          createAnnotation: () => createAnnotation(tickDate),
          point: groupedAnnotationPoints[tickDate]?.[0] ?? ({} as PointElement),
        });
      }
    });

    set(annotationDataset, "metadata.viewable", chartData);
    return chartData;
  }

  return [];
};

export type IconPosition = {
  width: number;
  height: number;
  x: number;
  y: number;
  svg: string;
  raw?: AnnotationDataset["data"];
};

export const checkIfOnIcon = (position: IconPosition, event: ChartEvent) => {
  const { x, y, height, width } = position;
  if (event.x && event.y) {
    return (
      event.x >= x &&
      event.x <= x + width &&
      event.y >= y &&
      event.y <= y + height
    );
  }
  return false;
};

export const drawIcon = (
  { ctx }: Chart,
  { x, y, height, width, svg }: IconPosition,
) => {
  ctx.save();
  const img = new Image();
  img.src = svg;
  ctx.drawImage(img, x, y, width, height);
  ctx.restore();
};

export const drawAnnotationIcons = (
  chart: Chart,
  options?: { force: boolean },
) => {
  getAnnotationData(chart, options).forEach((data: AnnotationData) => {
    if ("showAnnotations" in data) {
      const hasTrainingEvents =
        data.clusteredTrainingEventsCount &&
        data.clusteredTrainingEventsCount > 0;

      const iconPosition = buildMessageSvg({
        point: data.point,
        position: { x: data.xPosition, bottom: chart.chartArea.bottom },
        count: data.clusteredAnnotationCount ?? 0,
        ...(hasTrainingEvents && {
          iconColor: "#C825A8",
          iconColorHover: "#C825A8",
        }),
      });
      drawIcon(chart, iconPosition);
    }
  });
};
